/**
 * MobileGroupesTopMenu
 *
 * Mobile-optimized top navigation for GroupesView.
 * Replaces the desktop top menu when isMobile === 2 or 3.
 * Uses MUI Tabs for view switching and MUI IconButton for actions.
 */
import React from "react";
import {
  Box,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Button,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import Langs from "../../../app/lang/langs";
import { TeamSelector } from "../../../components/teams/TeamSelector";

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)";

const iconButtonSx = {
  color: "rgba(0,0,0,0.7)",
  minWidth: 44,
  minHeight: 44,
  borderRadius: "12px",
  transition: TRANSITION_SMOOTH,
  "&:hover": {
    backgroundColor: "rgba(255,144,21,0.08)",
    color: "rgb(255,144,21)",
    boxShadow: "0 2px 12px rgba(255,144,21,0.18)",
    transform: "scale(1.06)",
  },
  "&:active": {
    transform: "scale(0.96)",
  },
};

const primaryIconButtonSx = {
  ...iconButtonSx,
  "&:hover": {
    backgroundColor: "rgba(255,144,21,0.08)",
    color: "rgb(255,144,21)",
    transform: "scale(1.06)",
    boxShadow: "0 2px 12px rgba(255,144,21,0.18)",
  },
  "&:active": {
    transform: "scale(0.96)",
  },
};

/**
 * @param {Object}   props
 * @param {Array}    props.viewsOptions   - [{label, value}, ...]
 * @param {number}   props.selectedView   - current tab index (0, 1, or 2)
 * @param {Function} props.onViewChange   - (newValue) => void
 * @param {Function} props.onAddGroup     - () => void
 * @param {Function} props.onExport       - () => void
 * @param {Function} props.onAddMembers   - () => void
 * @param {Function} props.onAddManager   - () => void
 * @param {boolean}  props.canAddGroup    - whether the add group button is shown
 * @param {boolean}  props.canAddMembers  - whether the add members button is shown
 * @param {boolean}  props.canAddManager  - whether the add manager button is shown
 */
export default function MobileGroupesTopMenu({
  viewsOptions,
  selectedView,
  onViewChange,
  onAddGroup,
  onExport,
  onAddMembers,
  onAddManager,
  canAddGroup,
  canAddMembers,
  canAddManager,
  hasUnsavedChanges,
  onSave,
}) {
  const handleTabChange = (_event, newValue) => {
    onViewChange(newValue);
  };

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        bgcolor: "rgba(255,255,255,0.78)",
        backdropFilter: "blur(20px) saturate(1.4)",
        WebkitBackdropFilter: "blur(20px) saturate(1.4)",
        borderBottom: "1px solid rgba(0,40,128,0.06)",
        boxShadow:
          "0 1px 12px rgba(0,40,128,0.05), 0 0.5px 3px rgba(0,0,0,0.03)",
        px: 1.5,
        pt: 0.75,
        pb: 0,
        transition: TRANSITION_SMOOTH,
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: 0,
          left: "5%",
          right: "5%",
          height: "1px",
          background:
            "linear-gradient(90deg, transparent, rgba(255,144,21,0.15), transparent)",
        },
      }}
    >
      {/* Action buttons row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: 48,
          px: 0.5,
        }}
      >
        <Box
          sx={{
            fontWeight: 700,
            fontSize: "1.15rem",
            color: "rgb(0,0,0)",
            pl: 0.5,
            letterSpacing: "-0.025em",
            transition: "color 0.2s ease",
          }}
        >
          {viewsOptions.find((o) => o.value == selectedView)?.label}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {/* Save button — appears blinking when there are unsaved changes */}
          {hasUnsavedChanges && (
            <Button
              onClick={onSave}
              variant="contained"
              size="small"
              startIcon={<SaveOutlinedIcon />}
              sx={{
                bgcolor: "#FF9015",
                color: "#fff",
                fontWeight: 700,
                fontSize: "0.8rem",
                borderRadius: "10px",
                textTransform: "none",
                px: 1.5,
                py: 0.75,
                boxShadow: "0 2px 10px rgba(255,144,21,0.4)",
                "@keyframes blink": {
                  "0%, 100%": { boxShadow: "0 2px 10px rgba(255,144,21,0.4)", transform: "scale(1)" },
                  "50%": { boxShadow: "0 2px 18px rgba(255,144,21,0.75)", transform: "scale(1.05)" },
                },
                animation: "blink 1.1s ease-in-out infinite",
                "&:hover": { animation: "none", bgcolor: "#e07800" },
              }}
            >
              <Langs str="SAVE" />
            </Button>
          )}
          {/* Export button (Collabs tab only) */}
          {selectedView == 1 && (
            <Tooltip title="Export">
              <IconButton
                onClick={onExport}
                size="large"
                sx={iconButtonSx}
              >
                <FileDownloadOutlinedIcon />
              </IconButton>
            </Tooltip>
          )}

          {/* Add Group button (Groupes tab) */}
          {canAddGroup && selectedView == 0 && (
            <Tooltip title={<Langs str="Create new group" />}>
              <IconButton
                onClick={onAddGroup}
                size="large"
                sx={primaryIconButtonSx}
              >
                <AddCircleOutlineIcon />
              </IconButton>
            </Tooltip>
          )}

          {/* Add Members button (Collabs tab) */}
          {canAddMembers && selectedView == 1 && (
            <Tooltip title={<Langs str="ADD_MEMBERS" />}>
              <IconButton
                onClick={onAddMembers}
                size="large"
                sx={iconButtonSx}
              >
                <PersonAddAltOutlinedIcon />
              </IconButton>
            </Tooltip>
          )}

          {/* Add Manager button (Managers tab) */}
          {canAddManager && selectedView == 2 && (
            <Tooltip title={<Langs str="ADD_MANAGER" />}>
              <IconButton
                onClick={onAddManager}
                size="large"
                sx={primaryIconButtonSx}
              >
                <PersonAddAltOutlinedIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Team selector */}
      <Box
        sx={{
          px: 0.5,
          pb: 0.75,
          "& .transparent-select, & select, & .css-1s2u09g-control, & > div": {
            fontSize: "14px",
            borderRadius: "10px",
          },
        }}
      >
        <TeamSelector includeMyTeam />
      </Box>

      {/* Tabs row for view switching */}
      <Tabs
        value={selectedView}
        onChange={handleTabChange}
        variant="fullWidth"
        centered
        sx={{
          minHeight: 40,
          "& .MuiTab-root": {
            minHeight: 40,
            minWidth: 0,
            px: 1.5,
            py: 0.5,
            fontSize: "0.8rem",
            fontWeight: 600,
            textTransform: "none",
            color: "rgba(255,144,21,0.55)",
            borderRadius: "10px 10px 0 0",
            transition: TRANSITION_SMOOTH,
            "&:hover": {
              color: "rgba(255,144,21,0.8)",
              backgroundColor: "rgba(255,144,21,0.06)",
            },
            "&.Mui-selected": {
              fontWeight: 700,
              color: "rgb(255,144,21)",
            },
          },
          "& .MuiTabs-indicator": {
            height: 3,
            borderRadius: "3px 3px 0 0",
            background: "linear-gradient(90deg, #FF8701, #FFC100)",
            boxShadow: "0 0 8px rgba(255,144,21,0.35)",
            transition: "all 350ms cubic-bezier(0.4, 0, 0.2, 1)",
          },
        }}
      >
        {viewsOptions.map((option) => (
          <Tab
            key={option.value}
            label={option.label}
            value={option.value}
          />
        ))}
      </Tabs>
    </Box>
  );
}
